import { Bracket, BracketSeries, GameId } from "@portal-frontend-ssr/blast-api";
import { DateTime } from "luxon";
import { useMemo, useRef } from "react";
import { FindWonLostDrawMatchesOutput, findWonLostDrawMatches } from "./helpers/findWonLostDrawMatches";
import classNames from "classnames";
import { Accordion, Header, Icon, ContentWrapper } from "../../../Accordion";
import { generateImageUrl } from "@portal-frontend-ssr/blast-assets-api";
import { AdaptiveLink, useBreakpoints } from "@portal-frontend-ssr/ui";
import { IconButton } from "../../../IconButton";
import { generateMatchPageUrl } from "~/shared/helpers/url/generateMatchPageUrl";
import { MatchPageLinkWrapper } from "~/components/MatchPageLinkWrapper";
import { ClientOnly } from "remix-utils/client-only";
import { getDateRangeLabel } from "../../helpers/getDateRangeLabel";

interface RoundRobinBracketProps {
  bracket: Bracket;
  shouldBracketExpand: boolean;
  gameId: GameId;
}

export const RoundRobinBracket = ({ bracket, shouldBracketExpand, gameId }: RoundRobinBracketProps) => {
  const { isMobile } = useBreakpoints();
  const usedPlacements = useRef<number[]>([]);

  const teamStats = useMemo<
    {
      teamId: string;
      teamName: string;
      teamShortName: string | undefined;
      teamNationality: string;
      stats: FindWonLostDrawMatchesOutput;
    }[]
  >(() => {
    if (!bracket.matches) return [];

    const teamMatches: {
      [teamId: string]: {
        teamName: string;
        teamShortName: string | undefined;
        teamNationality: string;
        matches: BracketSeries[];
      };
    } = {};

    bracket.matches.forEach((match) => {
      if (match.teamA?.uuid) {
        if (teamMatches[match.teamA.uuid] === undefined) {
          teamMatches[match.teamA.uuid] = {
            teamName: match.teamA.name,
            teamShortName: match.teamA.shorthand,
            teamNationality: match.teamA.location || "",
            matches: [],
          };
        }

        teamMatches[match.teamA.uuid].matches.push(match);
      }
      if (match.teamB?.uuid) {
        if (teamMatches[match.teamB.uuid] === undefined) {
          teamMatches[match.teamB.uuid] = {
            teamName: match.teamB.name,
            teamShortName: match.teamB.shorthand,
            teamNationality: match.teamB.location || "",
            matches: [],
          };
        }

        teamMatches[match.teamB.uuid].matches.push(match);
      }
    });

    return Object.keys(teamMatches)
      .map((teamId) => {
        const { teamName, teamShortName, teamNationality, matches } = teamMatches[teamId];

        return {
          teamId,
          teamName,
          teamShortName,
          teamNationality,
          stats: findWonLostDrawMatches({ teamId, matches }),
        };
      })
      .sort((a, b) => b.stats.points - a.stats.points);
  }, [bracket]);

  if (bracket.format !== "round-robin") return;
  const numberOfTeamsAdvances = bracket.metadata?.numberOfTeamsAdvances;
  const numberOfMatchesPerTeam = bracket?.numberOfTeams ? bracket.numberOfTeams - 1 : 0;

  return (
    <Accordion className="!m-0" isExpandedByDefault={shouldBracketExpand}>
      <Header className="hover:bg-background-85">
        {/* Bracket Header (ex. Group A...) */}
        <div className="flex flex-row items-center gap-5">
          <span className="text-white font-style-label-l4">{bracket.label}</span>
          <div className="flex h-4 items-center justify-center rounded-small bg-white px-2">
            <ClientOnly fallback={<div className="w-16" />}>
              {() => (
                <span className="text-background-100 font-style-label-l4">
                  {getDateRangeLabel({ startDate: bracket.startDate, endDate: bracket.endDate })}
                </span>
              )}
            </ClientOnly>
          </div>
        </div>
        <Icon isTertiary />
      </Header>
      <ContentWrapper className="overflow-scroll !p-0 !pb-3">
        {/* Scoreboard & Advancing/Eliminated description */}
        <div className="mt-3 flex flex-col">
          {/* Advancing/Eliminated description */}
          <div className="mb-3 flex flex-row gap-5">
            <div className="flex flex-row items-center gap-2">
              <div className="size-2 rounded-small bg-success/50" />
              <span className="text-neutral font-style-label-l4">Advancing</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="size-2 rounded-small bg-error/50" />
              <span className="text-neutral font-style-label-l4">Eliminated</span>
            </div>
          </div>
          {/* Scoreboard */}
          <div className="flex flex-col gap-1">
            {teamStats.length === 0 &&
              Array.from({ length: bracket.numberOfTeams || 0 }).map((_, index) => {
                return (
                  <div
                    key={index}
                    className="flex w-full flex-row justify-between rounded-small bg-background-95 hover:group-hover:bg-background-90"
                  >
                    {/* Team information (logo, name & nationality) */}
                    <div className="flex flex-row items-center gap-3">
                      <div
                        className={classNames(
                          "flex h-9 w-10 items-center justify-center rounded-l-small bg-background-90",
                          {
                            "bg-success/50": numberOfTeamsAdvances !== undefined && index + 1 <= numberOfTeamsAdvances,
                            "bg-error/50": numberOfTeamsAdvances !== undefined && index + 1 > numberOfTeamsAdvances,
                          },
                        )}
                      >
                        <span className="text-white font-style-label-l4">#{index + 1}</span>
                      </div>
                      <span className="text-white font-style-body-b3">???</span>
                    </div>
                    {/* Match results overview */}
                    <div className="flex flex-row items-center gap-3 pr-5">
                      <div className="hidden flex-row gap-1 sd:flex">
                        {Array.from({ length: numberOfMatchesPerTeam }).map((_, index) => (
                          <div
                            key={index}
                            className={"flex size-4 items-center justify-center rounded-full bg-neutral"}
                          >
                            &nbsp;
                          </div>
                        ))}
                      </div>
                      {/* Points */}
                      <div className="w-12 text-right font-style-label-l4">
                        0<span className="ml-2 text-neutral">PTS</span>
                      </div>
                      {/* Expanding button */}
                      <IconButton icon="down" isDisabled isTertiary className="!size-5 opacity-50" />
                    </div>
                  </div>
                );
              })}

            {/* Team rows */}
            {teamStats.map((teamStat, i) => {
              const {
                teamId,
                teamName,
                teamNationality,
                stats: { wonMatches, lostMatches, drawMatches, scheduledMatches, points },
              } = teamStat;

              const matches = [...wonMatches, ...lostMatches, ...drawMatches, ...scheduledMatches].sort(
                (a, b) =>
                  DateTime.fromISO(a.timeOfSeries).toUnixInteger() - DateTime.fromISO(b.timeOfSeries).toUnixInteger(),
              );

              const getTeamPlacement = (): number => {
                // The teams are already sorted by the amount of points, so first would always have the most points
                if (i === 0) {
                  usedPlacements.current.push(1);
                  return 1;
                }

                // If the points are the same as previous team, then the placement is the same
                if (teamStat.stats.points === teamStats[i - 1].stats.points) {
                  return usedPlacements.current[usedPlacements.current.length - 1];
                }

                // If the teams has a lower amount of points then the team above them in the sorted array,
                // they would be placed in a new placement
                usedPlacements.current.push(i + 1);
                return i + 1;
              };
              return (
                <Accordion key={`${teamId}`} className="!mb-0">
                  <Header className="group !cursor-pointer !px-0">
                    <div className="flex size-full flex-row justify-between rounded-small bg-background-95 hover:group-hover:bg-background-90">
                      {/* Team information (logo, name & nationality) */}
                      <div className="flex flex-row items-center gap-3">
                        <div
                          className={classNames(
                            "flex h-full w-10 items-center justify-center rounded-l-small bg-background-90",
                            {
                              "bg-success/50": numberOfTeamsAdvances !== undefined && i + 1 <= numberOfTeamsAdvances,
                              "bg-error/50": numberOfTeamsAdvances !== undefined && i + 1 > numberOfTeamsAdvances,
                            },
                          )}
                        >
                          <span className="text-white font-style-label-l4">#{getTeamPlacement()}</span>
                        </div>
                        <div className="size-5">
                          <img
                            src={generateImageUrl("teams", teamId, {
                              format: "auto",
                              width: 24 + "px",
                            })}
                            alt=""
                            className="size-6 object-contain"
                          />
                        </div>
                        <span className="text-white font-style-body-b3">{teamName}</span>
                        <span className="hidden text-neutral font-style-label-l4 sd:block">{teamNationality}</span>
                      </div>
                      {/* Match results overview */}
                      <div className="flex flex-row items-center gap-3 pr-5">
                        <div className="hidden flex-row gap-1 sd:flex">
                          {matches.map((match, index) => (
                            <div
                              key={index}
                              className={classNames("flex size-4 items-center justify-center rounded-full", {
                                "bg-success": match.status === "won",
                                "bg-error": match.status === "lost",
                                "bg-neutral": match.status === "draw",
                                "bg-neutral/15": match.status === "scheduled",
                              })}
                            >
                              <span className="text-white font-style-label-l4">
                                {match.status === "won"
                                  ? "W"
                                  : match.status === "lost"
                                    ? "L"
                                    : match.status === "draw"
                                      ? "D"
                                      : "?"}
                              </span>
                            </div>
                          ))}
                        </div>
                        {/* Points */}
                        <div className="w-12 text-right font-style-label-l4">
                          {points}
                          <span className="ml-2 text-neutral">PTS</span>
                        </div>
                        {/* Expanding button */}
                        <Icon isTertiary className="!sd:size-7 !size-5" />
                      </div>
                    </div>
                  </Header>
                  <ContentWrapper className="!p-0">
                    {/* Team matches detailed view */}
                    <div className="mt-1 flex flex-col gap-1">
                      {matches.map((match, index) => {
                        if (!match.teamA || !match.teamB) return;
                        const matchTime = DateTime.fromISO(match.timeOfSeries);
                        const matchPageLink = generateMatchPageUrl({
                          game: gameId,
                          tournamentName: bracket.tournamentName,
                          matchUuid: match.uuid,
                          matchName: match.name,
                          teamAName: match.teamA.shorthand,
                          teamBName: match.teamB.shorthand,
                        });
                        return (
                          <MatchPageLinkWrapper
                            key={index}
                            condition={matchPageLink !== undefined}
                            wrapper={(children) => <AdaptiveLink to={matchPageLink ?? ""}>{children}</AdaptiveLink>}
                          >
                            <div className="flex h-9 w-full flex-row items-center justify-between rounded-small bg-background-95 px-5 hover:bg-background-90">
                              {/* Date of match */}
                              <ClientOnly
                                fallback={<div className="h-3 w-16 animate-pulse rounded-full bg-background-80" />}
                              >
                                {() => (
                                  <span className="hidden text-white font-style-label-l3 sd:block">
                                    {matchTime.toFormat("dd/LL")}
                                  </span>
                                )}
                              </ClientOnly>

                              {/* Information about team A (name & logo) */}
                              <div className="mr-3 flex grow basis-0 flex-row items-center justify-end gap-3 sd:mr-5 sd:gap-5">
                                <span className="text-right uppercase text-white font-style-body-b3 sd:normal-case">
                                  {isMobile ? match.teamA.shorthand || match.teamA.name : match.teamA.name}
                                </span>
                                <img
                                  src={generateImageUrl("teams", match.teamA.uuid, {
                                    format: "auto",
                                    width: 24 + "px",
                                  })}
                                  alt=""
                                  className="size-5 object-contain"
                                />
                              </div>

                              {/* Match score */}
                              <div className="flex w-11 justify-center rounded-small bg-background-100 py-1 sd:w-24">
                                <span
                                  className={classNames("font-style-label-l3", {
                                    "text-success": match.status === "won",
                                    "text-error": match.status === "lost",
                                    "text-white": match.status === "draw",
                                    "text-neutral": match.status === "scheduled",
                                  })}
                                >
                                  {match.status === "scheduled" ? "vs" : `${match.teamAScore} : ${match.teamBScore}`}
                                </span>
                              </div>

                              {/* Information about team B (name & logo) */}
                              <div className="ml-3 flex grow basis-0 flex-row items-center justify-start gap-3 sd:ml-5 sd:gap-5">
                                <div className="size-6">
                                  <img
                                    src={generateImageUrl("teams", match.teamB.uuid, {
                                      format: "auto",
                                      width: 24 + "px",
                                    })}
                                    alt=""
                                    className="size-6 object-contain"
                                  />
                                </div>
                                <span className="uppercase text-white font-style-body-b3 sd:normal-case">
                                  {isMobile ? match.teamB.shorthand || match.teamB.name : match.teamB.name}
                                </span>
                              </div>

                              {/* Match points for team */}
                              <div className="hidden w-fit min-w-10 font-style-label-l3 sd:flex">
                                {match.status !== "scheduled" && (
                                  <span
                                    className={classNames("sd:flex sd:w-full sd:justify-end", {
                                      "text-success": match.status === "won",
                                      "text-error": match.status === "lost",
                                      "text-white": match.status === "draw",
                                    })}
                                  >
                                    {match.points === 0 ? "0PT" : `+${match.points}PT`}
                                  </span>
                                )}
                              </div>
                            </div>
                          </MatchPageLinkWrapper>
                        );
                      })}
                    </div>
                  </ContentWrapper>
                </Accordion>
              );
            })}
          </div>
        </div>
      </ContentWrapper>
    </Accordion>
  );
};
